export type SourceType =
  | 'fire_history'
  | 'vegetation'
  | 'asset'
  | 'treatment'
  /** Roads/tracks cut out of the vegetation layer as non-burnable corridors. */
  | 'roads'
  /** Cadastre / parcel layers whose polygons become a project's property
   *  boundaries — alongside, or instead of, a file uploaded at creation. */
  | 'property_boundary'
  | 'hotspot'
  | 'lightning'
  | 'hazard'
  | 'ignition'
  | 'burnt_area';

/** Source types polled continuously by the realtime poller rather than
 *  pulled once at project init. The one list every console surface should
 *  read instead of spelling the kinds out again. */
export const REALTIME_SOURCE_TYPES: SourceType[] = [
  'hotspot',
  'lightning',
  'hazard',
  'ignition',
  'burnt_area',
];

/** Realtime kinds fed by the organisation's own ArcGIS feature layer. */
export const CUSTOM_REALTIME_SOURCE_TYPES: SourceType[] = ['ignition', 'burnt_area'];

export const isRealtimeSourceType = (type: string | undefined): boolean =>
  REALTIME_SOURCE_TYPES.includes(type as SourceType);

/** Display names, shared by the console's tabs, table and form. */
export const SOURCE_TYPE_LABELS: Record<SourceType, string> = {
  fire_history: 'Fire History',
  vegetation: 'Vegetation',
  asset: 'Assets',
  treatment: 'Treatments',
  roads: 'Roads',
  property_boundary: 'Property Boundaries',
  hotspot: 'Hotspots',
  lightning: 'Lightning',
  hazard: 'Hazards Incidents',
  ignition: 'Ignition Points',
  burnt_area: 'Burnt Areas',
};

/** Types pulled once at project init — the console's per-type tabs. */
export const INIT_TIME_SOURCE_TYPES: SourceType[] = [
  'fire_history',
  'vegetation',
  'asset',
  'treatment',
  'roads',
  'property_boundary',
];

/** Source types whose line features are buffered to a configured width
 *  (config.line_width_m / width_column) and can carry attribute filters
 *  (config.filters): treatments loaded as corridors, and roads cut out of
 *  the vegetation. */
export const CORRIDOR_SOURCE_TYPES: SourceType[] = ['treatment', 'roads'];

/** Source types that can carry attribute filters (config.filters): the
 *  corridor types plus property boundaries, which are polygons only and so
 *  have no line width. */
export const FILTERABLE_SOURCE_TYPES: SourceType[] = [...CORRIDOR_SOURCE_TYPES, 'property_boundary'];

export type LogicalField =
  | 'fire_name'
  | 'start_date'
  | 'end_date'
  | 'veg_class'
  | 'veg_date'
  | 'name'
  | 'fbp_pc'
  | 'fbp_pdf';

export const LOGICAL_FIELD_LABELS: Record<LogicalField, string> = {
  fire_name:  'Fire Name',
  start_date: 'Start Date',
  end_date:   'End Date',
  veg_class:  'Vegetation Class',
  veg_date:   'Veg Date',
  name:       'Name',
  fbp_pc:     'FBP Percent Conifer',
  fbp_pdf:    'FBP Percent Dead Fir',
};

export const LOGICAL_FIELDS_BY_SOURCE_TYPE: Record<SourceType, LogicalField[]> = {
  fire_history: ['fire_name', 'start_date', 'end_date'],
  vegetation:   ['veg_class', 'fbp_pc', 'fbp_pdf'],
  asset:        [],
  treatment:    ['name'],
  roads:        [],
  property_boundary: ['name'],
  hotspot:      [],
  lightning:    [],
  hazard:       [],
  ignition:     [],
  burnt_area:   [],
};

/** One attribute-filter clause on a data source (config.filters), ANDed with
 *  the others when the source is ingested. Mirrors
 *  api/services/data_sources/source_filters.py. */
export type SourceFilterOp =
  | 'eq'
  | 'ne'
  | 'in'
  | 'not_in'
  | 'contains'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'is_null'
  | 'not_null';

export interface SourceFilter {
  column: string;
  op: SourceFilterOp;
  value?: string;
}

export const SOURCE_FILTER_OP_LABELS: Record<SourceFilterOp, string> = {
  eq: 'equals',
  ne: 'does not equal',
  in: 'is one of',
  not_in: 'is not one of',
  contains: 'contains',
  gt: 'greater than',
  gte: 'at least',
  lt: 'less than',
  lte: 'at most',
  is_null: 'is empty',
  not_null: 'is not empty',
};

/** Operators that take no value. */
export const VALUELESS_FILTER_OPS: SourceFilterOp[] = ['is_null', 'not_null'];

export interface FieldMapping {
  id?: number;
  logical_field: LogicalField;
  source_column: string;
  date_format?: string;
}

export type AdapterType =
  | 'arcgis_feature_server'
  | 'arcgis_image_server'
  | 'shapefile'
  | 'raster_with_lookup'
  | 'nafi_multi_shapefile'
  | 'mapbox_vector_tiles'
  | 'overture'
  | 'firms_hotspots'
  | 'dea_hotspots'
  | 'xweather_lightning'
  | 'synthetic_lightning'
  | 'simulated_hotspot'
  | 'simulated_lightning'
  | 'nsw_rfs_hazards'
  | 'vic_emv_hazards'
  | 'qld_qfes_hazards'
  | 'sa_cfs_hazards'
  | 'wa_dfes_hazards'
  | 'act_esa_hazards';

/** Provider accesses spent on one day, as attributed to the viewer's tenant. */
export interface RealtimeUsageDay {
  day: string; // YYYY-MM-DD (UTC)
  accesses: number;
  /** Polls where the area had strikes and the full strike query ran. */
  active_polls: number;
}

export interface RealtimeUsage {
  today: number;
  /** Newest first, up to 30 days; days with no polling are absent. */
  days: RealtimeUsageDay[];
}

export interface RealtimeStatus {
  next_due_at: string | null;
  watermark: string | null;
  last_polled_at: string | null;
  last_success_at: string | null;
  last_error: string | null;
  consecutive_failures: number;
  /** Only metered providers (Xweather) report this; null when nothing has been spent. */
  usage?: RealtimeUsage | null;
}

export interface DataSource {
  id: number;
  name: string;
  source_type: SourceType;
  adapter_type: AdapterType;
  api_url: string | null;
  file_path: string | null;
  lookup_table_path: string | null;
  extent: object | null;
  tenant: number | null;
  asset_type: string | null;
  config: Record<string, unknown>;
  credentials?: Record<string, unknown>;
  has_credentials: boolean;
  is_builtin: boolean;
  field_mappings: FieldMapping[];
  realtime_status?: RealtimeStatus | null;
}

export interface DataSourceFormData {
  name: string;
  source_type: SourceType;
  adapter_type: AdapterType;
  api_url?: string;
  file_path?: string;
  lookup_table_path?: string;
  asset_type?: string;
  config?: Record<string, unknown>;
  credentials?: {
    portal_url?: string;
    username?: string;
    password?: string;
    item_id?: string;
    layer_index?: number;
    map_key?: string;
    client_id?: string;
    client_secret?: string;
    /** ArcGIS realtime layer: a static token / API key instead of a login. */
    token?: string;
    token_url?: string;
  };
  field_mappings?: FieldMapping[];
}

/** GET /api/data_sources/{id}/preview: the features a project over a
 *  bounding box would load from a property boundary source, named as the
 *  ingest names them. `truncated` means the source holds more than the
 *  preview cap; the full set still loads when the project is created. */
export interface DataSourcePreview {
  type: 'FeatureCollection';
  features: GeoJSON.Feature[];
  count: number;
  truncated: boolean;
}

/** One layer as described by POST /api/data_sources/arcgis/inspect. */
export interface ArcgisInspectedLayer {
  id: number | null;
  name: string | null;
  url: string;
  geometry_type?: string | null;
  suggested_kind?: 'ignition' | 'burnt_area' | null;
  fields?: { name: string; type: string; alias?: string | null }[];
  date_fields?: string[];
  object_id_field?: string | null;
  global_id_field?: string | null;
  edit_date_field?: string | null;
  creation_date_field?: string | null;
  max_record_count?: number | null;
  feature_count?: number;
  suggested?: {
    time_field: string | null;
    id_field: string | null;
    title_field: string | null;
  };
  problems: string[];
}

export interface ArcgisInspectResult {
  service_url: string | null;
  authenticated: boolean;
  layers: ArcgisInspectedLayer[];
}

/** The distinct values one layer field holds, from
 *  POST /api/data_sources/arcgis/field_values — what a treatment source's
 *  value → vegetation type mapping offers instead of typed guesses. */
export interface ArcgisFieldValuesResult {
  field: string;
  values: string[];
  /** The layer holds more values than were returned. */
  truncated: boolean;
}
